<template>
	<div :class="['pp', {'pp-small': mode == 'small' || mode == 'latest'}, {'pp-big': mode == 'big'}, {'pp-menu': mode == 'menu'}]">
		<figure class="pp-image">
			<NuxtLink :to="item.url_without_domain">
				<BaseUiImage loading="lazy" default="/images/no-image-475.webp" :data="imageData" />
			</NuxtLink>
		</figure>
		<div class="pp-cnt">
			<div class="pp-category" v-if="item.category_url_without_domain">
				<NuxtLink :to="item.category_url_without_domain">{{ item.category_title }}</NuxtLink>
			</div>

			<h2 class="pp-title">
				<NuxtLink :to="item.url_without_domain">{{ item.title }}</NuxtLink>
			</h2>
			<ClientOnly>
				<div v-if="shortDescription && item.short_description && (mode == 'small' || mode == 'latest' || mode == 'related')" class="pp-short-desc" v-html="limitWords(stripHtml(item.short_description), 18, '...')" />
				<div v-else-if="shortDescription && item.short_description && mode != 'menu'" class="pp-short-desc" v-html="limitWords(stripHtml(item.short_description), 40, '...')" />
			</ClientOnly>
		</div>
	</div>
</template>

<script setup>
	const {limitWords, stripHtml} = useText();
	const props = defineProps(['item', 'shortDescription', 'mode']);
	const {mobileSmallBreakpoint} = inject('rwd');

	const imageData = computed(() => {
		if(props.mode == 'latest' && mobileSmallBreakpoint.value == true) {
			return props.item.main_image_upload_path_thumb_mobile;
		}
		return props.item.main_image_upload_path_thumb;
	});
</script>

<style lang="less" scoped>
	.pp {
		display: flex;
		flex-direction: column;
		height: auto;
		width: calc(~'100% / 3 - 10px');
		margin: 0 5px 40px;
		padding: 10px 10px 10px;
		background: @white;
		position: relative;
		.transition(all);
		&:hover {
			box-shadow: 0 8px 30px 0 rgba(1, 61, 112, 0.25);
			z-index: 1;
		}

		@media (max-width: @t) {
			width: calc(~'100% / 3 - 15px');
			margin: 0 7px 40px;
			padding: 0;
			&:hover {
				box-shadow: none;
				z-index: 1;
			}
		}
		@media (max-width: @m) {
			width: calc(~'100% / 3 - 12px');
			margin: 0 6px 40px;
		}
		@media (max-width: @ms) {
			width: 100%;
			margin: 0 0 20px;
			padding: 0;
		}
	}
	.pp-image {
		display: flex;
		align-items: center;
		justify-content: center;
		flex-shrink: 0;
		:deep(a) {
			display: flex;
			align-items: center;
			justify-content: center;
			img {
				width: auto;
				height: auto;
				max-width: 100%;
				max-height: 100%;
				display: block;
			}
		}
	}
	.pp-cnt {
		width: 100%;
		padding: 20px 30px;

		@media (max-width: @l) {
			padding: 15px 20px;
		}
		@media (max-width: @t) {
			padding: 12px 15px 10px;
		}
		@media (max-width: @m) {
			padding: 10px;
		}
		@media (max-width: @ms) {
			width: 100%;
			padding: 10px 15px;
		}
	}
	.pp-category {
		font-size: 11px;
		line-height: 1.4;
		font-weight: 600;
		color: @blue;
		text-transform: uppercase;
		padding-bottom: 6px;
		opacity: 0.6;
		:deep(a) {
			color: @blue;
			text-decoration: none;
			&:hover {
				text-decoration: underline;
			}
		}

		@media (max-width: @t) {
			padding-bottom: 4px;
			:deep(a:hover) {
				text-decoration: none;
			}
		}
		@media (max-width: @ms) {
			font-size: 11px;
			line-height: 17px;
			padding-bottom: 3px;
		}
	}
	.pp-title {
		font-size: 20px;
		line-height: 1.4;
		font-weight: normal;
		letter-spacing: -0.4px;
		padding: 0;
		:deep(a) {
			color: @textColor;
			&:hover {
				color: @blue;
			}
		}

		@media (max-width: @l) {
			font-size: 18px;
		}
		@media (max-width: @t) {
			font-size: 16px;
			letter-spacing: -0.3px;
			:deep(a:hover) {
				color: @textColor;
			}
		}
		@media (max-width: @m) {
			font-size: 14px;
		}
		@media (max-width: @ms) {
			font-size: 16px;
			line-height: 22px;
		}
	}
	.pp-short-desc {
		font-size: 16px;
		line-height: 1.4;
		padding-top: 8px;

		@media (max-width: @t) {
			font-size: 13px;
			padding-top: 5px;
		}
		@media (max-width: @m) {
			font-size: 12px;
			padding-top: 4px;
		}
		@media (max-width: @ms) {
			display: none;
			padding: 0;
		}
	}

	.pp-big {
		display: flex;
		align-items: center;
		flex-direction: row;
		width: 100%;
		padding: 0;
		margin: 0;

		.pp-cnt {
			padding: 0 40px 20px 100px;
		}
		.pp-category {
			font-size: 12px;
		}
		.pp-title {
			font-size: 28px;
			letter-spacing: -0.3px;
		}

		@media (max-width: @l) {
			.pp-cnt {
				padding: 0 20px 0 65px;
			}
			.pp-image :deep(a) {
				max-width: 600px;
			}
		}
		@media (max-width: @t) {
			width: 100%;
			margin: 0;
			padding: 0;
			.pp-image a {
				max-width: 460px;
			}
			.pp-cnt {
				padding: 0 0 0 30px;
			}
			.pp-title {
				font-size: 20px;
				letter-spacing: -0.25px;
			}
		}
		@media (max-width: @m) {
			display: flex;
			align-items: center;
			flex-direction: row;
			width: 100%;
			padding: 0;
			margin: 0;

			.pp-image a {
				max-width: 360px;
			}
			.pp-cnt {
				padding: 0 0 0 20px;
			}
			.pp-category {
				font-size: 11px;
			}
			.pp-title {
				font-size: 16px;
			}
		}
		@media (max-width: @ms) {
			flex-direction: column;
			width: calc(~'100% - -30px');
			margin-left: -15px;
			.pp-image a {
				max-width: unset;
			}
			.pp-cnt {
				width: 100%;
				padding: 10px 15px;
			}
			.pp-category {
				font-size: 11px;
				line-height: 17px;
				padding-bottom: 3px;
			}
			.pp-title {
				font-size: 16px;
			}
			.pp-short-desc {
				display: block;
			}
		}
	}

	.pp-menu {
		align-items: center;
		flex-direction: row;
		width: 100%;
		margin: 0;
		padding: 7px 40px 7px 33px;
		transition: none;
		&:hover {
			background: rgba(0, 93, 172, 0.05);
			box-shadow: none;
			z-index: 0;
			.pp-category a,
			.pp-title a {
				color: @blue;
			}
		}

		.pp-image {
			width: 170px;
			margin-right: 25px;
			:deep(a) {
				flex-grow: 1;
			}
		}
		.pp-cnt {
			padding: 0;
		}
		.pp-category {
			margin-bottom: 5px;
			padding: 0;
			font-size: 13px;
			font-weight: 600;
			color: @blue;
			text-transform: none;
			opacity: 1;
		}
		.pp-title {
			font-size: 15px;
			line-height: 1.4;
			font-weight: normal;
			letter-spacing: normal;
			&:hover {
				text-decoration: underline;
			}
		}

		@media (max-width: @l) {
			padding: 7px 30px 7px 30px;

			.pp-image {
				width: 150px;
			}
		}
		@media (max-width: @t) {
			margin: 0 0 15px;
			padding: 0 15px;
			&:hover {
				background: white;
				.pp-title a {
					color: @textColor;
				}
			}
			.pp-image {
				width: 130px;
				margin-right: 18px;
			}
			.pp-title {
				font-size: 13px;
				line-height: 1.3;
			}
		}
	}

	.pd-related {
		.pp {
			width: 100%;
			margin: 5px;
			flex-direction: row;
			align-items: center;
		}
		.pp-image {
			width: 360px;
			max-height: 173px;
		}
		.pp-cnt {
			padding: 0 35px;
		}
		.pp-title {
			font-size: 18px;
		}

		@media (max-width: @t) {
			.pp {
				width: 100%;
				margin: 0 0 20px;
			}
			.pp-image {
				width: 310px;
				max-height: 155px;
			}
			.pp-cnt {
				padding: 0 0 0 30px;
			}
			.pp-title {
				font-size: 16px;
			}
		}
		@media (max-width: @ms) {
			padding: 15px 0 35px;
			.pp {
				width: 100%;
				margin: 0 0 20px;
				display: block;
			}
			.pp-image {
				width: 100%;
				max-height: unset;
			}
			.pp-cnt {
				padding: 10px 15px;
			}
		}
	}

	.pw-items {
		display: flex;

		.pp {
			margin: 0;
		}
		.pp-big {
			flex-direction: column;
			padding: 10px;
			&:hover {
				box-shadow: 0 8px 30px 0 rgba(1, 61, 112, 0.25);
			}
			.pp-cnt {
				padding: 25px 40px 20px;
			}
		}
		.pp-small {
			flex-direction: row;
			width: 100%;
			align-items: center;
			.pp-cnt {
				padding: 5px 25px;
			}
			.pp-category {
				padding-bottom: 2px;
			}
			.pp-title {
				font-size: 18px;
			}
		}

		@media (max-width: @l) {
			.pp-big {
				.pp-cnt {
					padding: 20px 30px 20px;
				}
			}
			.pp-small {
				.pp-image {
					width: 126px;
				}
				.pp-cnt {
					padding: 5px 15px;
				}
				.pp-title {
					font-size: 15px;
				}
			}
		}
		@media (max-width: @t) {
			.pp {
				margin: 0;
			}
			.pp-big {
				padding: 0 10px 0 0;
				&:hover {
					box-shadow: none;
				}
				.pp-image a {
					max-width: unset;
				}
				.pp-cnt {
					width: 100%;
					padding: 15px 20px 10px;
				}
			}
			.pp-small {
				padding-bottom: 10px;
				.pp-image {
					width: 109px;
				}
				.pp-cnt {
					padding: 0 0 0 20px;
				}
				.pp-title {
					font-size: 14px;
				}
			}
		}
		@media (max-width: @m) {
			.pp {
				margin: 0;
			}
			.pp-big {
				.pp-title {
					font-size: 20px;
				}
			}
			.pp-small {
				.pp-image {
					width: 104px;
				}
				.pp-title {
					font-size: 13px;
				}
			}
		}
		@media (max-width: @ms) {
			.pp {
				margin: 0 0 20px;
			}
			.pp-big {
				width: 100%;
				padding: 0;
				.pp-cnt {
					padding: 10px 15px;
				}
				.pp-title {
					font-size: 16px;
				}
				.pp-short-desc {
					display: none;
					padding: 0;
				}
			}
			.pp-small {
				flex-direction: column;
				padding-bottom: 15px;

				.pp-image {
					width: 100%;
				}
				.pp-cnt {
					padding: 10px 15px;
				}
				.pp-title {
					font-size: 16px;
				}
			}
		}
	}
</style>
